import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  FluentProvider,
  OverlayDrawer,
  Skeleton,
  SkeletonItem,
  Text,
  makeStyles,
  webLightTheme,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowClockwise24Regular,
  Dismiss24Regular,
  NumberSymbol24Regular,
  DataBarVertical24Regular,
} from '@fluentui/react-icons';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleSettingsList } from '@moko/ui';
import { groupPanelsByType, type PanelDefinition } from './panelCatalog';
import type { DashboardComponentType } from '../../../services/dashboardService';

const useStyles = makeStyles({
  // Per-row entity icon, rendered inside each toggle's label node.
  rowIcon: {
    display: 'inline-flex',
    color: tokens.colorBrandForeground1,
  },
  rowLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  // The list itself is indented under the (flush) section header so the indent
  // reads as hierarchy, not a shift of the whole group.
  list: {
    paddingLeft: tokens.spacingHorizontalL,
  },
  groups: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalM,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground1,
  },
  sectionIcon: {
    display: 'inline-flex',
    color: tokens.colorNeutralForeground1,
  },
  sectionLabel: {
    fontWeight: tokens.fontWeightBold,
    textTransform: 'uppercase',
    fontSize: tokens.fontSizeBase300,
    letterSpacing: '0.04em',
  },
  state: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalXXL} 0`,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  skeletonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
  },
});

// Icon shown next to each chart-type section header.
const TYPE_ICONS: Record<DashboardComponentType, ReactNode> = {
  COUNT: <NumberSymbol24Regular />,
  BARCHART: <DataBarVertical24Regular />,
};

interface CustomizeDrawerProps {
  open: boolean;
  onClose: () => void;
  panels: PanelDefinition[];
  enabledPanels: string[];
  onToggle: (key: string, enabled: boolean) => void;
  loading: boolean;
  failed: boolean;
  onReload: () => void;
}

const CustomizeDrawer = ({
  open,
  onClose,
  panels,
  enabledPanels,
  onToggle,
  loading,
  failed,
  onReload,
}: CustomizeDrawerProps) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const renderContent = () => {
    if (loading) {
      return (
        <Skeleton>
          <div className={styles.list}>
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className={styles.skeletonRow}>
                <SkeletonItem shape="circle" size={24} />
                <SkeletonItem size={16} style={{ flex: 1 }} />
                <SkeletonItem size={24} style={{ width: 40 }} />
              </div>
            ))}
          </div>
        </Skeleton>
      );
    }

    if (failed) {
      return (
        <div className={styles.state}>
          <Text>
            {t(
              'failedToLoadComponents',
              'Could not load dashboard components.'
            )}
          </Text>
          <Button
            appearance="secondary"
            icon={<ArrowClockwise24Regular />}
            onClick={onReload}
          >
            {t('retry', 'Retry')}
          </Button>
        </div>
      );
    }

    if (panels.length === 0) {
      return (
        <div className={styles.state}>
          <Text>{t('noComponentsAvailable', 'No components available.')}</Text>
        </div>
      );
    }

    return (
      <div className={styles.groups}>
        {groupPanelsByType(panels).map((group) => (
          <div key={group.type} className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIcon}>
                {TYPE_ICONS[group.type]}
              </span>
              <span className={styles.sectionLabel}>{group.label}</span>
            </div>
            {/* Reuse the shared toggle list for the rows only; the section
                header is rendered above so it stays flush while the rows
                (the list) are indented under it. */}
            <ToggleSettingsList
              className={styles.list}
              items={group.panels.map((panel) => ({
                key: panel.key,
                label: (
                  <span className={styles.rowLabel}>
                    <span className={styles.rowIcon}>{panel.icon}</span>
                    <Text>{t(panel.key, panel.title)}</Text>
                  </span>
                ),
              }))}
              values={Object.fromEntries(
                group.panels.map((panel) => [
                  panel.key,
                  enabledPanels.includes(panel.key),
                ])
              )}
              onChange={onToggle}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <OverlayDrawer
        position="end"
        open={open}
        onOpenChange={(_, data) => {
          if (!data.open) onClose();
        }}
        size="small"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={t('close', 'Close')}
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            {t('customizeDashboard', 'Customize dashboard')}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>{renderContent()}</DrawerBody>
      </OverlayDrawer>
    </FluentProvider>
  );
};

export default CustomizeDrawer;
